/* ── Microsoft Graph API + PKCE OAuth helper ──────────────────────────────── */

const MS_AUTH    = 'https://login.microsoftonline.com/common/oauth2/v2.0';
const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';
const SCOPES     = 'offline_access openid profile User.Read Mail.Send Calendars.ReadWrite';

const TOKEN_KEY   = 'ms_outlook_token';
const CLIENT_KEY  = 'ms_outlook_client_id';
const PKCE_KEY    = 'ms_pkce_verifier';

// ── PKCE helpers ──────────────────────────────────────────────────────────────

function b64url(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function genVerifier() {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return b64url(arr.buffer);
}

async function genChallenge(verifier) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return b64url(buf);
}

// ── Storage ───────────────────────────────────────────────────────────────────

export function loadClientId()    { return localStorage.getItem(CLIENT_KEY) || ''; }
export function saveClientId(id)  { localStorage.setItem(CLIENT_KEY, id); }

export function loadToken() {
  try { const s = localStorage.getItem(TOKEN_KEY); if (s) return JSON.parse(s); } catch {}
  return null;
}
function saveToken(data) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify({
    ...data,
    expiresAt: Date.now() + (data.expires_in || 3600) * 1000,
  }));
}
export function clearToken() { localStorage.removeItem(TOKEN_KEY); }

export function isOutlookConnected() {
  const t = loadToken();
  return !!(t?.access_token);
}

export function getOutlookUser() {
  return loadToken()?.userInfo || null;
}

// ── OAuth flow ────────────────────────────────────────────────────────────────

export async function initiateOutlookAuth(clientId) {
  const verifier   = genVerifier();
  const challenge  = await genChallenge(verifier);
  const redirectUri = window.location.origin + window.location.pathname;

  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, clientId, redirectUri }));

  const params = new URLSearchParams({
    client_id:             clientId,
    response_type:         'code',
    redirect_uri:          redirectUri,
    scope:                 SCOPES,
    response_mode:         'query',
    code_challenge:        challenge,
    code_challenge_method: 'S256',
    prompt:                'select_account',
  });

  window.location.href = `${MS_AUTH}/authorize?${params}`;
}

// Called on app load when ?code=... is in URL
export async function handleOutlookCallback(code) {
  const raw = sessionStorage.getItem(PKCE_KEY);
  if (!raw) throw new Error('PKCE-State fehlt. Bitte erneut verbinden.');
  const { verifier, clientId, redirectUri } = JSON.parse(raw);

  const body = new URLSearchParams({
    client_id:     clientId,
    grant_type:    'authorization_code',
    code,
    redirect_uri:  redirectUri,
    code_verifier: verifier,
    scope:         SCOPES,
  });

  const res = await fetch(`${MS_AUTH}/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body:    body.toString(),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error_description || 'Token-Austausch fehlgeschlagen');
  }

  const tokenData = await res.json();

  // Fetch user profile
  let userInfo = null;
  try {
    const uRes = await fetch(`${GRAPH_BASE}/me`, {
      headers: { Authorization: `Bearer ${tokenData.access_token}` },
    });
    if (uRes.ok) userInfo = await uRes.json();
  } catch {}

  saveToken({ ...tokenData, userInfo });
  sessionStorage.removeItem(PKCE_KEY);
  return userInfo;
}

// ── Token management ──────────────────────────────────────────────────────────

async function refreshToken() {
  const token = loadToken();
  if (!token?.refresh_token) { clearToken(); throw new Error('Kein Refresh-Token'); }
  const clientId = loadClientId();

  const body = new URLSearchParams({
    client_id:     clientId,
    grant_type:    'refresh_token',
    refresh_token: token.refresh_token,
    scope:         SCOPES,
  });

  const res = await fetch(`${MS_AUTH}/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body:    body.toString(),
  });

  if (!res.ok) { clearToken(); throw new Error('Token-Refresh fehlgeschlagen'); }
  const newToken = await res.json();
  saveToken({ ...token, ...newToken });
  return newToken.access_token;
}

export async function getAccessToken() {
  const token = loadToken();
  if (!token) throw new Error('Nicht mit Outlook verbunden');

  // Refresh 5 minutes before expiry
  if (token.expiresAt && token.expiresAt < Date.now() + 5 * 60 * 1000) {
    return await refreshToken();
  }
  return token.access_token;
}

// ── Mail ─────────────────────────────────────────────────────────────────────

export async function sendOutlookMail({ to, subject, body }) {
  const accessToken = await getAccessToken();

  const payload = {
    message: {
      subject,
      body:          { contentType: 'Text', content: body },
      toRecipients:  to ? [{ emailAddress: { address: to } }] : [],
    },
    saveToSentItems: true,
  };

  const res = await fetch(`${GRAPH_BASE}/me/sendMail`, {
    method:  'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || 'E-Mail konnte nicht gesendet werden');
  }
}

// ── Calendar ──────────────────────────────────────────────────────────────────

// Convert Outlook dateTime string (local, no Z) to ISO for our CRM
function olDateToISO(olDate, timeZone = 'Europe/Berlin') {
  if (!olDate) return null;
  // Outlook returns "2024-01-15T10:00:00.0000000" – treat as Berlin time
  const clean = olDate.replace(/\.\d+$/, ''); // remove fractional seconds
  try {
    // Parse as-if local time in Berlin by appending offset
    // We rely on the server returning Berlin time via Prefer header
    return new Date(clean).toISOString();
  } catch { return null; }
}

export async function getOutlookCalendarEvents(startISO, endISO) {
  const accessToken = await getAccessToken();

  const params = new URLSearchParams({
    startDateTime: startISO,
    endDateTime:   endISO,
    $select:       'id,subject,start,end,location,bodyPreview,isAllDay,isCancelled',
    $orderby:      'start/dateTime',
    $top:          '100',
  });

  const res = await fetch(`${GRAPH_BASE}/me/calendarView?${params}`, {
    headers: {
      Authorization:       `Bearer ${accessToken}`,
      Prefer:              'outlook.timezone="Europe/Berlin"',
      'odata.maxpagesize': '100',
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Kalender konnte nicht geladen werden');
  }

  const data = await res.json();
  return (data.value || [])
    .filter(ev => !ev.isCancelled)
    .map(ev => {
      const startDT = ev.start?.dateTime || '';
      const endDT   = ev.end?.dateTime   || '';
      const dateISO = startDT.slice(0, 10);
      const startTime = startDT.slice(11, 16);  // "HH:MM"
      const endTime   = endDT.slice(11, 16);
      return {
        id:        'ol_' + ev.id,
        title:     ev.subject || '(Kein Titel)',
        dateISO,
        startISO:  startDT,
        endISO:    endDT,
        date:      dateISO ? dateISO.split('-').reverse().join('.') : '',
        startTime,
        endTime,
        location:  ev.location?.displayName || '',
        description: ev.bodyPreview || '',
        isOutlook: true,
        isAllDay:  ev.isAllDay,
        meetingType: 'Outlook',
      };
    });
}

export async function createOutlookCalendarEvent(meeting) {
  const accessToken = await getAccessToken();

  const payload = {
    subject: meeting.title || 'Termin',
    body:    { contentType: 'Text', content: meeting.description || '' },
    start:   { dateTime: meeting.startISO || meeting.dateISO, timeZone: 'Europe/Berlin' },
    end:     { dateTime: meeting.endISO   || meeting.startISO || meeting.dateISO, timeZone: 'Europe/Berlin' },
    location:{ displayName: meeting.location || '' },
  };

  const res = await fetch(`${GRAPH_BASE}/me/events`, {
    method:  'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Event konnte nicht erstellt werden');
  }

  return await res.json();
}
